/**
 * Date filter types for session filtering
 */
export enum DateFilter {
  All = 'all',
  Today = 'today',
  ThisWeek = 'thisWeek',
  ThisMonth = 'thisMonth'
}

/**
 * Checks if a date is today
 * @param date - The date to check
 * @returns true if the date is today
 */
export const isToday = (date: Date): boolean => {
  const today = new Date();
  return date.toDateString() === today.toDateString();
};

/**
 * Checks if a date is within the current week (Monday to Sunday)
 * @param date - The date to check
 * @returns true if the date is within this week
 */
export const isThisWeek = (date: Date): boolean => {
  const today = new Date();
  const startOfWeek = new Date(today);
  const dayOfWeek = today.getDay();
  const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Adjust for Monday start
  startOfWeek.setDate(today.getDate() - diff);
  startOfWeek.setHours(0, 0, 0, 0);
  
  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 6);
  endOfWeek.setHours(23, 59, 59, 999);
  
  return date >= startOfWeek && date <= endOfWeek;
};

/**
 * Checks if a date is within the current month
 * @param date - The date to check
 * @returns true if the date is within this month
 */
export const isThisMonth = (date: Date): boolean => {
  const today = new Date();
  return date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
};

/**
 * Gets the date range for a given date filter
 * @param dateFilter - The date filter to get range for
 * @returns Object with startDate and endDate as ISO strings, or null for 'All' filter
 */
export const getDateRangeForFilter = (dateFilter: DateFilter): { startDate: string; endDate: string } | null => {
  if (dateFilter === DateFilter.All) {
    return null;
  }

  const today = new Date();
  let startDate: Date;
  let endDate: Date;

  switch (dateFilter) {
    case DateFilter.Today:
      startDate = new Date(today);
      startDate.setHours(0, 0, 0, 0);
      endDate = new Date(today);
      endDate.setHours(23, 59, 59, 999);
      break;

    case DateFilter.ThisWeek:
      startDate = new Date(today);
      const dayOfWeek = today.getDay();
      const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Adjust for Monday start
      startDate.setDate(today.getDate() - diff);
      startDate.setHours(0, 0, 0, 0);
      
      endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 6);
      endDate.setHours(23, 59, 59, 999);
      break;

    case DateFilter.ThisMonth:
      startDate = new Date(today.getFullYear(), today.getMonth(), 1);
      startDate.setHours(0, 0, 0, 0);
      
      endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      endDate.setHours(23, 59, 59, 999);
      break;

    default:
      return null;
  }

  return {
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString()
  };
};

